
import React, { useState } from 'react';
import { Eye, Trash2, Copy, Check, Clock, Calendar } from 'lucide-react';
import { OAuthClientDto } from '../../../../services/api';

interface OAuthClientsListProps {
  clients: OAuthClientDto[];
  isLoading: boolean;
  onView: (id: string) => void;
  onRevoke: (id: string) => void;
}

export const OAuthClientsList: React.FC<OAuthClientsListProps> = ({ 
  clients, 
  isLoading, 
  onView, 
  onRevoke 
}) => {
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleCopy = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('es-ES');
  };

  const getRelativeTime = (dateString: string | undefined) => {
    if (!dateString) return 'Nunca';
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return 'hace unos segundos';
    if (diffInSeconds < 3600) return `hace ${Math.floor(diffInSeconds / 60)} min`;
    if (diffInSeconds < 86400) return `hace ${Math.floor(diffInSeconds / 3600)} horas`;
    return formatDate(dateString);
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'PrintAgent': return 'bg-blue-100 text-blue-800';
      case 'Integration': return 'bg-purple-100 text-purple-800';
      case 'MobileApp': return 'bg-green-100 text-green-800';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (clients.length === 0) {
    return (
      <div className="text-center p-8 bg-slate-50 rounded-lg border border-slate-200 border-dashed">
        <p className="text-slate-500">No hay clientes registrados</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm text-left">
        <thead className="bg-slate-50 text-slate-500 font-medium border-b border-slate-200">
          <tr>
            <th className="px-4 py-3">Nombre / Tipo</th>
            <th className="px-4 py-3">Client ID</th>
            <th className="px-4 py-3">Estado</th>
            <th className="px-4 py-3">Uso</th>
            <th className="px-4 py-3 text-right">Acciones</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {clients.map((client) => (
            <tr key={client.id} className="hover:bg-slate-50 group">
              <td className="px-4 py-3">
                <div className="font-medium text-slate-900">{client.name}</div>
                <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium mt-1 ${getTypeColor(client.type)}`}>
                  {client.type}
                </span>
                {client.description && (
                  <div className="text-xs text-slate-500 mt-0.5 max-w-xs truncate">{client.description}</div>
                )}
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs font-mono select-all">
                    {client.clientId}
                  </code>
                  <button
                    onClick={() => handleCopy(client.clientId, client.id)}
                    className="text-slate-400 hover:text-slate-600 transition-colors"
                    title="Copiar Client ID"
                  >
                    {copiedId === client.id ? <Check size={14} className="text-green-600" /> : <Copy size={14} />}
                  </button>
                </div>
              </td>
              <td className="px-4 py-3">
                {client.isActive ? (
                  <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                    Activo
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>
                    Inactivo
                  </span>
                )}
              </td>
              <td className="px-4 py-3 text-slate-500">
                <div className="flex flex-col gap-0.5 text-xs">
                  <div className="flex items-center gap-1" title={`Último uso: ${formatDate(client.lastUsedAt)}`}>
                    <Clock size={12} />
                    {getRelativeTime(client.lastUsedAt)}
                  </div>
                  <div className="flex items-center gap-1" title={`Creado: ${formatDate(client.createdAt)}`}>
                    <Calendar size={12} />
                    {new Date(client.createdAt).toLocaleDateString()}
                  </div>
                </div>
              </td>
              <td className="px-4 py-3 text-right">
                <div className="flex items-center justify-end gap-2">
                  <button
                    onClick={() => onView(client.id)}
                    className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                    title="Ver detalles"
                  >
                    <Eye size={16} />
                  </button>
                  {client.isActive && (
                    <button
                      onClick={() => onRevoke(client.id)}
                      className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                      title="Revocar acceso"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
